import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop } from 'services/ui';
import { Colors } from 'context';
import { CareerIcon } from 'atoms';
import { CareerCapability, useShare } from 'core';

interface ICareerLockedSection {
  capability: CareerCapability;
}

/**
 * Раздел, закрытый этому зрителю (ТЗ 0069).
 *
 * 🔴 Это замена 404, и заменяет она его целиком. Раньше не-участник, зашедший
 * на `/career/learning`, получал страницу «не найдено» — то есть продукт
 * отвечал ему «такого раздела нет» вместо «раздел откроется участникам, вот
 * как им стать». Из-за этого же продающий экран приходилось держать на
 * отдельном корне `/growth`.
 *
 * Правила блока:
 *
 * * заголовок, честное «почему» и РОВНО ОДИН призыв. Два призыва означают, что
 *   мы не решили, чего хотим от человека;
 * * ссылка приходит С СЕРВЕРА (`myShareOptions[0].targetUrl`) — адрес школы
 *   задаётся настройкой бэкенда, и зашитый в код он однажды разойдётся;
 * * блок НЕ помечен `aria-disabled` и не выкинут из табуляции: он содержит
 *   настоящий текст и настоящую ссылку, а «отключённый» контейнер читалка
 *   пропустила бы вместе с объяснением.
 */
export const CareerLockedSection = memo(({ capability }: ICareerLockedSection) => {
  const { t, i18n } = useTranslation();
  // Тот же язык, что и у остальных потребителей: с разными переменными Apollo
  // считает это разными запросами и ходит в сеть дважды.
  const { options } = useShare(i18n.language === 'ru' ? 'RUS' : 'POL');
  const siteUrl = options[0]?.targetUrl ?? '';

  return (
    <Card>
      <Head>
        <CareerIcon name="lock" size={22} />
        <Title>{t(`careerCenter.locked.${capability}.title`)}</Title>
      </Head>
      <Text>{t(`careerCenter.locked.${capability}.text`)}</Text>
      <How>{t('careerCenter.locked.how')}</How>
      {siteUrl ? (
        <Action href={siteUrl} target="_blank" rel="noopener noreferrer">
          {t('careerCenter.locked.action')}
        </Action>
      ) : null}
    </Card>
  );
});

const Card = styled.section`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px 20px;
  border: 1px dashed ${Colors.main};
  border-radius: 16px;
  background: ${Colors.white};

  ${desktop} {
    padding: 32px 28px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  margin: 0;
  ${fontService.text({ size: 20, lineHeight: 130, weight: FontWeight.bold })}
`;

const Text = styled.p`
  margin: 0;
  max-width: 640px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const How = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Action = styled.a`
  align-self: flex-start;
  padding: 10px 20px;
  border-radius: 8px;
  /* Жёлтый — фон, текст — btn_text: в тёмной теме Colors.black равен #ECECEC
     и на жёлтом становится нечитаемым. Бэктиков внутри шаблонной строки быть
     не может — они обрывают её на разборе. */
  background: ${Colors.main};
  color: ${Colors.btn_text};
  text-decoration: none;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.btn_text};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;
